import { z } from 'zod';
import { DomainError } from '../errors.js';
import {
  type CashDirection,
  type CashTxnType,
  cashAmountSchema,
  cashCurrencySchema,
  cashDaySchema,
  cashParser,
  cashText,
} from './cash.js';

/**
 * Cash to Master (kasa.pdf §2; ADR 0093 §3): kaptan nakit ister, ofis inceler ve onaylar, ödeme
 * ya da acente teslimini düzenler, kaptan alındığını teyit eder; teyit kasaya tek gelir kaydıdır.
 * Talep, onay ve transfer kasa bakiyesini artırmaz. Saf modül.
 */

/** Onay matrisinde CTM talebinin kayıt türü. */
export const CTM_RECORD = 'ctm_request';

/** Harcama gerekçeleri (kasa.pdf §2), ortağın sırasıyla. */
export const CTM_PURPOSES = [
  'crew_cash_advance',
  'crew_wages',
  'port_expenses',
  'provisions',
  'bonded_stores',
  'medical_expenses',
  'transportation',
  'repairs',
  'emergency_expenses',
  'other',
] as const;
export type CtmPurpose = (typeof CTM_PURPOSES)[number];
export const CTM_DELIVERY_METHODS = ['agent', 'bank', 'other'] as const;
export type CtmDeliveryMethod = (typeof CTM_DELIVERY_METHODS)[number];

export const CTM_STATUSES = [
  'draft',
  'pending',
  'returned',
  'approved',
  'transferred',
  'received',
  'cancelled',
] as const;
export type CtmStatus = (typeof CTM_STATUSES)[number];

export const CTM_NO = /^CTM-\d{4}-\d{3,}$/;
export const CTM_CANCEL_REASON = { min: 5, max: 500 } as const;

/** Gemi ve yıl başına sıradaki numara (`CTM-2026-015`). */
export function nextCtmNo(year: string, existing: readonly string[]): string {
  const max = existing.reduce((acc, no) => {
    const match = new RegExp(`^CTM-${year}-(\\d+)$`).exec(no);
    return match ? Math.max(acc, Number(match[1])) : acc;
  }, 0);
  return `CTM-${year}-${String(max + 1).padStart(3, '0')}`;
}

const missing = (input: unknown) => input === undefined || input === null;
const typed = (c: string) => ({
  error: (issue: { input?: unknown }) => (missing(issue.input) ? 'REQUIRED' : c),
});

const requestSchema = z
  .object({
    port: z
      .string(typed('INVALID_TEXT'))
      .transform((v) => v.normalize('NFC').trim())
      .pipe(z.string().min(1, 'REQUIRED').max(200, 'TOO_LONG')),
    requiredDate: cashDaySchema(),
    currency: cashCurrencySchema(),
    requestedAmount: cashAmountSchema(1),
    estimatedExpenses: cashAmountSchema(0).nullish().default(null),
    purpose: z.enum(CTM_PURPOSES, typed('INVALID_OPTION')),
    deliveryMethod: z.enum(CTM_DELIVERY_METHODS, typed('INVALID_OPTION')),
    remarks: cashText(2000),
  })
  .superRefine((value, ctx) => {
    // "Other" ne için olduğunu yazar
    if (value.purpose === 'other' && value.remarks === null) {
      ctx.addIssue({ code: 'custom', path: ['remarks'], message: 'REQUIRED' });
    }
  });
export type CtmRequestInput = z.output<typeof requestSchema>;
/** Talep formunun alanları; numara, gün, isteyen ve kasa bakiyesi sistemden. */
export const parseCtmRequest = cashParser(requestSchema);

/** Gönderimde ihtiyaç tarihi talep gününden önce olamaz. */
export function assertCtmSubmittable(content: { requiredDate: string }, today: string): void {
  if (content.requiredDate < today) throw new DomainError('REQUIRED_DATE_PAST');
}

/** Onay içeriği: hash'lenen alanlar (kasa bakiyesi anlık bilgidir, içerik değildir). */
export interface CtmContent {
  vesselId: string;
  ctmNo: string;
  requestDate: string;
  requestedBy: string;
  port: string;
  requiredDate: string;
  currency: string;
  requestedAmount: number;
  estimatedExpenses: number | null;
  purpose: string;
  deliveryMethod: string;
  remarks: string | null;
}

export function ctmContent(input: CtmContent): CtmContent {
  return {
    vesselId: input.vesselId,
    ctmNo: input.ctmNo,
    requestDate: input.requestDate,
    requestedBy: input.requestedBy,
    port: input.port,
    requiredDate: input.requiredDate,
    currency: input.currency,
    requestedAmount: input.requestedAmount,
    estimatedExpenses: input.estimatedExpenses,
    purpose: input.purpose,
    deliveryMethod: input.deliveryMethod,
    remarks: input.remarks,
  };
}

/** Durumun girdileri: onay durumu ve akışın değiştirilemez kayıtları. */
export interface CtmStatusInput {
  approval: 'draft' | 'pending' | 'returned' | 'approved' | 'reapproval_pending';
  cancelled: boolean;
  transferred: boolean;
  /** Ters çevrilmemiş teyit işlemi var mı. */
  received: boolean;
}

/** Durum türetilir; saklanmaz (ADR 0093 §3). */
export function ctmDisplayStatus(input: CtmStatusInput): CtmStatus {
  if (input.cancelled) return 'cancelled';
  if (input.approval === 'approved') {
    if (input.received) return 'received';
    return input.transferred ? 'transferred' : 'approved';
  }
  if (input.approval === 'returned') return 'returned';
  if (input.approval === 'draft') return 'draft';
  return 'pending';
}

/** Ofis yalnız onaylı talebe bir kez ödeme düzenler. */
export function assertCtmTransferable(input: CtmStatusInput): void {
  if (input.cancelled) throw new DomainError('CTM_CANCELLED');
  if (input.approval !== 'approved') throw new DomainError('CTM_NOT_APPROVED');
  if (input.transferred) throw new DomainError('CTM_ALREADY_TRANSFERRED');
}

/**
 * Teyit edilebilir mi: ofis onayı şart; transfer kaydı şart değil (internetsiz gemiye ofisin
 * satırı nakitten sonra ulaşabilir; ADR 0093 §3, sorular #963). Talep başına bir canlı teyit.
 */
export function assertCtmReceivable(input: CtmStatusInput): void {
  if (input.cancelled) throw new DomainError('CTM_CANCELLED');
  if (input.approval !== 'approved') throw new DomainError('CTM_NOT_APPROVED');
  if (input.received) throw new DomainError('CTM_ALREADY_RECEIVED');
}

/** Teyitte beklenen tutar: ofisin transfer tutarı, yoksa talep edilen. */
export function ctmExpectedAmount(input: {
  requestedAmount: number;
  transferAmount: number | null;
}): number {
  return input.transferAmount ?? input.requestedAmount;
}

/**
 * CTM'nin kasaya tek kaydı (KRİTİK KURAL): teyit yoksa hiçbir işlem yoktur; teyit edilmiş talep
 * için `ctm_received` girişi. Talep, onay ve transfer bakiyeyi değiştirmez.
 */
export function ctmReceiptTxn(
  input: CtmStatusInput,
  receipt?: { amount: number; currency: string },
): {
  txnType: CashTxnType;
  direction: CashDirection;
  category: 'ctm';
  amount: number;
  currency: string;
} | null {
  if (!receipt) return null;
  assertCtmReceivable(input);
  return {
    txnType: 'ctm_received',
    direction: 'in',
    category: 'ctm',
    amount: receipt.amount,
    currency: receipt.currency,
  };
}

/** Ofisin gönderdiği tutarın talepten farkının gerekçesi (ADR 0110 §2). */
const VARIANCE_REASON = { min: 5, max: 500 } as const;

const transferSchema = (requested: number | undefined) =>
  z
    .object({
      amount: cashAmountSchema(1),
      method: z.enum(CTM_DELIVERY_METHODS, typed('INVALID_OPTION')),
      arrangedOn: cashDaySchema(),
      reference: cashText(200),
      remarks: cashText(1000),
      varianceReason: cashText(VARIANCE_REASON.max),
    })
    .superRefine((value, ctx) => {
      // Kısmi ya da fazla gönderim kayda gerekçesiyle geçer (ADR 0110 §2)
      if (
        requested !== undefined &&
        value.amount !== requested &&
        (value.varianceReason ?? '').length < VARIANCE_REASON.min
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['varianceReason'],
          message: 'VARIANCE_REASON_REQUIRED',
        });
      }
    });
export type CtmTransferInput = z.output<ReturnType<typeof transferSchema>>;
/**
 * Ofisin ödeme düzenlemesi: gönderilen tutar, yöntem, gün, acente/banka referansı. Talep edilen
 * tutar verilirse gönderilen tutar ondan farklıysa gerekçe zorunlu (az ya da fazla; ADR 0110 §2).
 */
export function parseCtmTransfer(input: unknown, requested?: number) {
  return cashParser(transferSchema(requested))(input);
}

const TIME = /^([01]\d|2[0-3]):[0-5]\d$/;
const receiptSchema = (expected: number) =>
  z
    .object({
      amount: cashAmountSchema(1),
      day: cashDaySchema(),
      time: z.string().regex(TIME, 'INVALID_TIME').nullish(),
      receiptNo: cashText(100),
      remarks: cashText(1000),
    })
    .superRefine((value, ctx) => {
      // Alınan tutar beklenenden farklıysa gerekçe (ADR 0093 §3)
      if (value.amount !== expected && value.remarks === null) {
        ctx.addIssue({ code: 'custom', path: ['remarks'], message: 'RECEIPT_REASON_REQUIRED' });
      }
    })
    .transform((value) => ({ ...value, time: value.time ?? null }));
export type CtmReceiptInput = z.output<ReturnType<typeof receiptSchema>>;
/** Kaptanın teyidi: alınan tutar, gün ve saat, makbuz numarası, gerekçe. */
export function parseCtmReceipt(input: unknown, expected: number) {
  return cashParser(receiptSchema(expected))(input);
}

export type CtmStepKey = 'created' | 'review' | 'arranged' | 'received' | 'posted';
export type CtmStepState = 'done' | 'current' | 'waiting' | 'returned' | 'cancelled';

export interface CtmTimelineStep {
  key: CtmStepKey;
  state: CtmStepState;
  at: string | null;
}

/**
 * Onay ve durum zaman çizgisi (ekran 2): Request Created → Office Review → Payment Arranged →
 * Cash Received → Posted to Cash Account. Teyit ve kasa kaydı aynı andır.
 */
export function ctmTimeline(input: {
  status: CtmStatusInput;
  createdAt: string;
  submittedAt: string | null;
  reviewedAt: string | null;
  transferredAt: string | null;
  receivedAt: string | null;
}): CtmTimelineStep[] {
  const { status } = input;
  const display = ctmDisplayStatus(status);
  const approved = status.approval === 'approved';
  const review: CtmStepState = approved
    ? 'done'
    : status.cancelled
      ? 'cancelled'
      : status.approval === 'returned'
        ? 'returned'
        : status.approval === 'pending' || status.approval === 'reapproval_pending'
          ? 'current'
          : 'waiting';
  const arranged: CtmStepState = status.transferred
    ? 'done'
    : approved && !status.received && !status.cancelled
      ? 'current'
      : 'waiting';
  const received: CtmStepState =
    display === 'received' ? 'done' : approved && !status.cancelled ? 'current' : 'waiting';
  return [
    { key: 'created', state: 'done', at: input.submittedAt ?? input.createdAt },
    { key: 'review', state: review, at: input.reviewedAt },
    {
      key: 'arranged',
      // Teyit transfer satırından önce geldiyse adım atlanmış sayılır
      state: display === 'received' && !status.transferred ? 'waiting' : arranged,
      at: input.transferredAt,
    },
    { key: 'received', state: received, at: input.receivedAt },
    { key: 'posted', state: display === 'received' ? 'done' : 'waiting', at: input.receivedAt },
  ];
}

export interface CtmSummaryRow {
  currency: string;
  requested: number;
  approved: number;
  received: number;
  pending: number;
}

/**
 * Özet kartları (ekran 3) para birimine göre: Total Requested (gönderilmiş, iptal edilmemiş),
 * Total Approved (ofisin gönderdiği tutar; ADR 0110 §2), Total Received (teyit tutarları), Pending (ofis incelemesi bekleyen).
 */
export function ctmSummary(
  items: readonly {
    currency: string;
    requestedAmount: number;
    /** Ofisin gönderdiği tutar (ADR 0110 §2); yoksa talep edilen. */
    approvedAmount?: number | null;
    receivedAmount: number | null;
    status: CtmStatus;
  }[],
): CtmSummaryRow[] {
  const rows = new Map<string, CtmSummaryRow>();
  for (const item of items) {
    if (item.status === 'draft' || item.status === 'cancelled') continue;
    const row = rows.get(item.currency) ?? {
      currency: item.currency,
      requested: 0,
      approved: 0,
      received: 0,
      pending: 0,
    };
    row.requested += item.requestedAmount;
    if (item.status === 'approved' || item.status === 'transferred' || item.status === 'received') {
      row.approved += item.approvedAmount ?? item.requestedAmount;
    }
    if (item.status === 'pending') row.pending += item.requestedAmount;
    row.received += item.receivedAmount ?? 0;
    rows.set(item.currency, row);
  }
  return [...rows.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}
